<template>
  <div class="contract-info-wrapper">
    <!-- Header do contrato -->
    <div class="contract-header mb-4">
      <v-row>
        <v-col cols="12" md="6">
          <div class="contract-title">
            <v-icon color="primary" class="mr-2">mdi-file-contract</v-icon>
            <span class="text-h6 font-weight-bold">
              Contrato
              <div>{{ contractData.numContrato }}</div>
            </span>
          </div>
          <div class="mt-5">
            <v-chip color="primary" outlined medium>
              <v-icon left>mdi-cash</v-icon>
              {{ contractData.valorHora | currency }}/hora
            </v-chip>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="info-card" elevation="2">
            <v-card-text class="pb-2">
              <div class="d-flex align-center mb-2">
                <v-icon color="success" class="mr-2">mdi-calendar-range</v-icon>
                <span class="font-weight-medium text-body-1"
                  >Período do Contrato</span
                >
              </div>
              <div class="date-info">
                <div class="d-flex justify-space-between align-center">
                  <div class="date-block">
                    <span class="text-caption grey--text">Início</span>
                    <div class="text-h7 font-weight-bold primary--text">
                      {{
                        contractData.inicio ||
                        contractData.dataInicio | moment("DD/MM/YYYY")
                      }}
                    </div>
                  </div>
                  <v-icon color="grey">mdi-arrow-right</v-icon>
                  <div class="date-block text-center">
                    <span class="text-caption grey--text">Fim</span>
                    <div class="text-h7 font-weight-bold primary--text">
                      {{
                        contractData.fim ||
                        contractData.dataFim | moment("DD/MM/YYYY")
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Informações em cards -->
    <v-row>
      <!-- Card de Período -->

      <!-- Card de Descrição/Objeto -->
      <v-col cols="12">
        <v-card class="info-card" elevation="2">
          <v-card-text class="pb-2">
            <div class="d-flex align-center mb-2">
              <v-icon color="info" class="mr-2">mdi-text-box-outline</v-icon>
              <span class="font-weight-medium text-body-1">Objeto</span>
            </div>
            <div class="description-text">
              <div v-if="contractData.descricao">
                <p
                  class="text-body-2 mb-1"
                  v-if="!showFullDescription && isTruncated"
                >
                  {{ truncatedDescription }}
                  <v-btn
                    text
                    small
                    color="primary"
                    class="pa-0 ml-1"
                    @click="showFullDescription = true"
                    style="min-width: auto"
                  >
                    Ver mais
                  </v-btn>
                </p>

                <p class="text-body-2 mb-1" v-else-if="contractData.descricao">
                  {{ contractData.descricao }}
                  <v-btn
                    v-if="isTruncated"
                    text
                    small
                    color="primary"
                    class="pa-0 ml-1"
                    @click="showFullDescription = false"
                    style="min-width: auto"
                  >
                    Ver menos
                  </v-btn>
                </p>
              </div>
              <p class="text-body-2 grey--text mb-0" v-else>
                <em>Nenhum objeto informado</em>
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Estatísticas rápidas -->
    <v-row class="mt-2">
      <v-col cols="12">
        <div class="stats-section">
          <div class="d-flex align-center mb-3">
            <v-icon color="orange" class="mr-2">mdi-chart-line</v-icon>
            <span class="font-weight-medium text-body-1">Resumo</span>
          </div>
          <v-row>
            <v-col>
              <div class="stat-item">
                <div class="stat-value">
                  {{ contractData.numContrato || contractData.codigo || "N/A" }}
                </div>
                <div class="stat-label">Número</div>
              </div>
            </v-col>

            <v-col>
              <div class="stat-item">
                <div class="stat-value">
                  {{ contractData.qtdEntregasPrevistas || "N/A" }}
                </div>
                <div class="stat-label">Entregas Previstas</div>
              </div>
            </v-col>
            <v-col>
              <div class="stat-item">
                <div class="stat-value">
                  {{ contractData.qtdHorasPrevistas || "N/A" }}
                </div>
                <div class="stat-label">Horas Previstas</div>
              </div>
            </v-col>
          </v-row>

          <!-- Segunda linha de estatísticas -->
          <v-row class="mt-2">
            <v-col>
              <div class="stat-item">
                <div class="stat-value">
                  {{ calculateContractDays() }}
                </div>
                <div class="stat-label">Dias de Contrato</div>
              </div>
            </v-col>
            <v-col>
              <div class="stat-item">
                <div class="stat-value">
                  {{ contractData.valorHora | currency }}
                </div>
                <div class="stat-label">Valor/Hora</div>
              </div>
            </v-col>
            <v-col
              v-if="contractData.qtdHorasPrevistas && contractData.valorHora"
            >
              <div class="stat-item">
                <div class="stat-value primary--text">
                  {{ calculateTotalValue() | currency }}
                </div>
                <div class="stat-label">Valor Total Previsto</div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "ContractInfoCard",
  props: {
    contractData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    subtitle: {
      type: String,
      default: "Informações detalhadas do contrato",
    },
    status: {
      type: String,
      default: "Ativo",
    },
    maxDescriptionLength: {
      type: Number,
      default: 120,
    },
  },
  data() {
    return {
      showFullDescription: false,
    };
  },
  computed: {
    truncatedDescription() {
      if (!this.contractData.descricao) return "";
      if (this.contractData.descricao.length <= this.maxDescriptionLength) {
        return this.contractData.descricao;
      }
      return (
        this.contractData.descricao.substring(0, this.maxDescriptionLength) +
        "..."
      );
    },
    isTruncated() {
      return (
        this.contractData.descricao &&
        this.contractData.descricao.length >= this.maxDescriptionLength
      );
    },
  },
  filters: {
    currency(value) {
      if (!value) return "";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    },
  },
  methods: {
    calculateContractDays() {
      const startDate =
        this.contractData.inicio || this.contractData.dataInicio;
      const endDate = this.contractData.fim || this.contractData.dataFim;

      if (!startDate || !endDate) return "N/A";

      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    calculateTotalValue() {
      if (
        !this.contractData.qtdHorasPrevistas ||
        !this.contractData.valorHora
      ) {
        return 0;
      }
      return this.contractData.qtdHorasPrevistas * this.contractData.valorHora;
    },
  },
};
</script>

<style lang="scss" scoped>
.contract-info-wrapper {
  background-color: #fafbfc;
  border-radius: 8px;
  padding: 20px;
}

.contract-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #1976d2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.contract-title {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.info-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  border: 1px solid #e3e6ea;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }

  .v-card__text {
    padding: 20px;
  }
}

.date-info {
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.date-block {
  text-align: center;

  .text-caption {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.description-text {
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
  display: flex;
  align-items: flex-start;

  > div {
    width: 100%;
  }
}

.stats-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e3e6ea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-item {
  text-align: center;
  padding: 12px;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 24px;
}

.stat-label {
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

// Chip styling
:deep(.v-chip.v-chip--outlined) {
  background-color: rgba(25, 118, 210, 0.1) !important;
  border-color: #1976d2 !important;
}
</style>
